import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";

/* ------------------------------------------------------------------
   PRODUCT TOUR — recreated Nyra AI console screens (fictional data)
   All names / numbers / emails below are invented for the demo.
------------------------------------------------------------------- */

const TEAL = "#235F52";
const TEAL_2 = "#2E7D6B";
const MOSS = "#4A9683";

const NAV = [
    { key: "analytics",    label: "Analytics",              group: "Overview",         icon: "fa-solid fa-chart-column" },
    { key: "conversation", label: "Conversation Analytics", group: "Overview",         icon: "fa-solid fa-chart-pie" },
    { key: "agents",       label: "Agents",                 group: "Agent Management",  icon: "fa-solid fa-headset", badge: "8" },
    { key: "knowledge",    label: "Knowledge Base",         group: "Agent Management",  icon: "fa-solid fa-database" },
    { key: "calls",        label: "Call Logs",              group: "Call Center",       icon: "fa-solid fa-phone" },
    { key: "tickets",      label: "CRM Tickets",            group: "Call Center",       icon: "fa-solid fa-ticket" },
    { key: "outbound",     label: "Outbound",               group: "Telephony",         icon: "fa-solid fa-arrow-up-right-from-square" },
    { key: "inbound",      label: "Inbound",                group: "Telephony",         icon: "fa-solid fa-inbox" },
    { key: "campaigns",    label: "Bulk Campaigns",         group: "Telephony",         icon: "fa-solid fa-bullhorn" },
];

const AGENTS = [
    { name: "Meridian Health (Appointments)", meta: "Medical Services · Appointment Coordinator", active: true },
    { name: "Atlas Bank — Loan Recovery",     meta: "Financial Services · Customer Support" },
    { name: "Horizon Life — Claims Assistant", meta: "Insurance Services · Claims Assistant" },
    { name: "Nova Telecom — Support",          meta: "Telecom · Support Executive" },
    { name: "Orchard Finance — Collections",   meta: "Financial Services · Collections" },
    { name: "Summit Motors — Service Advisor", meta: "Automotive · Service Advisor" },
    { name: "Vertex Retail — Sales",           meta: "Retail · Sales Assistant" },
    { name: "Riverside NBFC — KYC",            meta: "Financial Services · Onboarding" },
];

const KBS = [
    { name: "Product Handbook", desc: "Core product knowledge & specs", type: "DOCUMENT", people: 4, date: "Jul 17, 2026" },
    { name: "Policy Library",   desc: "Approved policies & SOPs",       type: "DOCUMENT", people: 3, date: "Jun 28, 2026" },
    { name: "Support FAQ",      desc: "Common customer questions",      type: "URL",      people: 2, date: "Jun 08, 2026" },
    { name: "Onboarding Guide", desc: "New customer onboarding steps",  type: "DOCUMENT", people: 5, date: "May 25, 2026" },
    { name: "Pricing Sheet",    desc: "Current plans & pricing",        type: "DOCUMENT", people: 1, date: "May 25, 2026" },
    { name: "Compliance Docs",  desc: "Regulatory reference material",  type: "URL",      people: 2, date: "May 12, 2026" },
];

const CALLS = [
    { id: "c1a2f7b4-48…", agent: "Meridian Health (Appointments)", dur: "0m 41s", date: "Aug 18, 2026", time: "10:35:49 AM", status: "Bot Handled", ok: true },
    { id: "f8b3d9e2-2c…", agent: "Atlas Bank — Loan Recovery",     dur: "0m 33s", date: "Aug 12, 2026", time: "11:24:59 AM", status: "Bot Handled", ok: true },
    { id: "9d4e51a0-7f…", agent: "Horizon Life — Claims Assistant", dur: "1m 12s", date: "Aug 12, 2026", time: "11:24:37 AM", status: "Transferred", ok: false },
    { id: "3b7c88d1-1a…", agent: "Nova Telecom — Support",          dur: "0m 58s", date: "Aug 11, 2026", time: "04:12:03 PM", status: "Bot Handled", ok: true },
];

const TICKETS = [
    { id: "TCK-4821", concern: "Billing query",           agent: "Atlas Bank — Loan Recovery",     status: "Open",     date: "25 Jun 2026", time: "4:35 pm" },
    { id: "TCK-4820", concern: "Appointment reschedule",  agent: "Meridian Health (Appointments)", status: "Open",     date: "25 Jun 2026", time: "3:57 pm" },
    { id: "TCK-4802", concern: "Claim status follow-up",  agent: "Horizon Life — Claims Assistant", status: "Resolved", date: "20 May 2026", time: "11:14 am" },
    { id: "TCK-4791", concern: "Plan upgrade request",    agent: "Nova Telecom — Support",          status: "Open",     date: "20 May 2026", time: "11:09 am" },
];

const TRUNKS = [
    { name: "North Region Trunk", ip: "10.24.11.5:5060", number: "00911140000123", id: "ST_Dm4kZS…" },
    { name: "South Region Trunk", ip: "10.24.11.6:5060", number: "00911140000456", id: "ST_a3iPrr…" },
    { name: "West Region Trunk",  ip: "10.24.11.7:5060", number: "00911140000789", id: "ST_9qzWJJ…" },
];

/* -------------------------- small building blocks -------------------------- */

const StatCard = ({ icon, label, sub, value, tint = TEAL_2 }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
            <span className="w-9 h-9 rounded-lg grid place-items-center text-white text-sm shrink-0"
                style={{ background: `linear-gradient(140deg, ${tint}, ${TEAL})` }}>
                <i className={icon} />
            </span>
            <div>
                <div className="text-[13px] font-semibold text-slate-800 leading-tight">{label}</div>
                <div className="text-[11px] text-slate-400">{sub}</div>
            </div>
        </div>
        <div className="text-3xl font-bold tracking-tight text-slate-900">{value}</div>
        <div className="mt-3 h-1 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: "62%", background: MOSS }} />
        </div>
    </div>
);

const PanelHead = ({ title, sub }) => (
    <div className="mb-6">
        <h4 className="text-xl font-bold text-slate-900">{title}</h4>
        <p className="text-sm text-slate-500 mt-1">{sub}</p>
    </div>
);

/* -------------------------------- screens -------------------------------- */

const AnalyticsScreen = () => (
    <>
        <PanelHead title="Analytics" sub="Performance metrics for the selected time period" />
        <div className="flex flex-wrap gap-2 mb-6">
            {["Last 2 Hours", "Last 12 Hours", "Last 24 Hours", "Custom Range"].map((t, i) => (
                <span key={t} className={`text-xs font-medium px-3 py-1.5 rounded-lg ${i === 0 ? "text-white" : "text-slate-600 bg-slate-100"}`}
                    style={i === 0 ? { background: TEAL } : {}}>{t}</span>
            ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <StatCard icon="fa-solid fa-phone" label="Total Calls" sub="All voice interactions" value="1,248" />
            <StatCard icon="fa-solid fa-clock" label="Avg Duration" sub="Per conversation" value="2m 14s" tint={MOSS} />
            <StatCard icon="fa-solid fa-user-group" label="Transferred" sub="Escalated to agents" value="86" tint="#5FA88A" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-bold text-slate-800 mb-4">Performance Comparison</div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-slate-400 text-left">
                        <th className="pb-3 font-medium">Metric</th>
                        <th className="pb-3 font-medium">Current</th>
                        <th className="pb-3 font-medium">Previous</th>
                        <th className="pb-3 font-medium text-right">Change</th>
                    </tr>
                </thead>
                <tbody className="text-slate-700">
                    {[
                        ["Total Calls", "1,248", "1,102", "+13.2%", true],
                        ["Success Rate", "92.4%", "90.1%", "+2.3%", true],
                        ["Calls Transferred", "86", "94", "-8.5%", false],
                        ["Average Duration", "2m 14s", "2m 31s", "-11.3%", false],
                    ].map(([m, c, p, ch, up]) => (
                        <tr key={m} className="border-t border-slate-100">
                            <td className="py-3 font-medium text-slate-800">{m}</td>
                            <td className="py-3">{c}</td>
                            <td className="py-3 text-slate-500">{p}</td>
                            <td className="py-3 text-right font-medium" style={{ color: up ? "#2E7D6B" : "#B45309" }}>{ch}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </>
);

const ConversationScreen = () => (
    <>
        <PanelHead title="Conversation Analytics" sub="Centralised view for agent performance · Jul 21 – Aug 20, 2026" />
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <StatCard icon="fa-solid fa-message" label="Messages" sub="Conversations in range" value="9,412" />
            <StatCard icon="fa-solid fa-stopwatch" label="Avg Duration" sub="Across handled calls" value="1m 58s" tint={MOSS} />
            <StatCard icon="fa-solid fa-file-lines" label="Transcript Msgs" sub="Exchanged in transcripts" value="41,207" tint="#5FA88A" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-bold text-slate-800 mb-4">Intent distribution</div>
            <div className="space-y-3">
                {[["Appointment booking", 38], ["Billing & payments", 27], ["Policy / claim status", 19], ["General enquiry", 16]].map(([label, pct]) => (
                    <div key={label}>
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>{label}</span><span className="font-medium text-slate-800">{pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${TEAL}, ${MOSS})` }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </>
);

const AgentsScreen = () => (
    <>
        <PanelHead title="Agents" sub="8 active AI voice agents across your operations" />
        <div className="grid lg:grid-cols-5 gap-5">
            <div className="lg:col-span-2 space-y-2">
                {AGENTS.slice(0, 6).map((a) => (
                    <div key={a.name}
                        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${a.active ? "border-transparent" : "border-slate-200 bg-white"}`}
                        style={a.active ? { background: "rgba(35,95,82,0.08)", borderColor: TEAL_2 } : {}}>
                        <span className="w-8 h-8 rounded-lg grid place-items-center text-white text-xs shrink-0"
                            style={{ background: `linear-gradient(140deg, ${MOSS}, ${TEAL})` }}>
                            <i className="fa-solid fa-headset" />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-slate-800 truncate">{a.name}</div>
                            <div className="text-[11px] text-slate-400 truncate">{a.meta}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-sm font-bold text-slate-900">Meridian Health (Appointments)</div>
                        <div className="text-[11px] text-slate-400">Voice · Medical Services · Appointment Coordinator</div>
                    </div>
                    <span className="text-[11px] font-medium text-white px-2.5 py-1 rounded-full" style={{ background: TEAL_2 }}>Active</span>
                </div>
                <div className="flex gap-4 text-[12px] font-medium text-slate-500 border-b border-slate-100 pb-2 mb-4 overflow-x-auto">
                    {["Prompts", "Knowledge Base", "Tools", "Telephony", "Integrations"].map((t, i) => (
                        <span key={t} className={i === 0 ? "text-slate-900 border-b-2 pb-2 -mb-2.5" : "whitespace-nowrap"}
                            style={i === 0 ? { borderColor: TEAL } : {}}>{t}</span>
                    ))}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Greeting messages</div>
                <div className="space-y-2">
                    {[["English", "Hi! Thanks for calling Meridian Health. How can I help you today?"],
                      ["Hindi", "नमस्ते! मेरिडियन हेल्थ में आपका स्वागत है। मैं कैसे मदद करूँ?"]].map(([lang, msg]) => (
                        <div key={lang} className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
                            <span className="text-[11px] font-semibold text-slate-500 w-14 shrink-0">{lang}</span>
                            <span className="text-[12px] text-slate-700 truncate">{msg}</span>
                            <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded text-white shrink-0" style={{ background: MOSS }}>DEFAULT</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </>
);

const KnowledgeScreen = () => (
    <>
        <PanelHead title="Knowledge Base Manager" sub="Organise and manage your knowledge repositories" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {KBS.map((k) => (
                <div key={k.name} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between mb-2">
                        <div className="text-sm font-bold text-slate-900">{k.name}</div>
                        <i className="fa-solid fa-trash text-slate-300 text-xs" />
                    </div>
                    <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-3"
                        style={{ background: "rgba(35,95,82,0.10)", color: TEAL }}>READY</span>
                    <p className="text-[12px] text-slate-500 mb-4 leading-snug">{k.desc}</p>
                    <div className="flex items-center gap-4 text-[11px] text-slate-400">
                        <span><i className="fa-solid fa-file mr-1" />{k.type}</span>
                        <span><i className="fa-solid fa-user-group mr-1" />{k.people}</span>
                        <span className="ml-auto"><i className="fa-regular fa-calendar mr-1" />{k.date}</span>
                    </div>
                </div>
            ))}
        </div>
    </>
);

const CallsScreen = () => (
    <>
        <PanelHead title="Call Logs" sub="Search and manage voice call records" />
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {CALLS.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3.5">
                    <span className="w-9 h-9 rounded-lg grid place-items-center text-white text-xs shrink-0"
                        style={{ background: `linear-gradient(140deg, ${MOSS}, ${TEAL})` }}>
                        <i className="fa-solid fa-phone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-slate-800">Web</div>
                        <div className="text-[11px] text-slate-400 truncate">{c.id}</div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded border ${c.ok ? "border-slate-200 text-slate-600" : "border-amber-200 text-amber-700 bg-amber-50"}`}>
                        {c.status}
                    </span>
                    <div className="hidden sm:block text-[11px] text-slate-400 ml-2">
                        {c.date}<br />{c.time}
                    </div>
                    <div className="ml-auto text-right min-w-0">
                        <div className="text-[13px] font-semibold text-slate-800">{c.dur}</div>
                        <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{c.agent}</div>
                    </div>
                    <i className="fa-solid fa-arrow-right text-slate-300 text-xs" />
                </div>
            ))}
        </div>
    </>
);

const TicketsScreen = () => (
    <>
        <PanelHead title="CRM Tickets" sub="Track customer service tickets generated from voice calls" />
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
            {[["Total Tickets", "142"], ["Today", "6"], ["This Week", "38"], ["Departments", "9"]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-[11px] text-slate-400 mb-1">{l}</div>
                    <div className="text-2xl font-bold text-slate-900">{v}</div>
                </div>
            ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
                <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-slate-400 text-left bg-slate-50">
                        <th className="px-4 py-3 font-medium">Ticket</th>
                        <th className="px-4 py-3 font-medium">Concern</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium hidden md:table-cell">Agent</th>
                        <th className="px-4 py-3 font-medium hidden md:table-cell">Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {TICKETS.map((t) => (
                        <tr key={t.id} className="border-t border-slate-100">
                            <td className="px-4 py-3 font-mono text-[12px] text-slate-500">{t.id}</td>
                            <td className="px-4 py-3 font-medium text-slate-800">{t.concern}</td>
                            <td className="px-4 py-3">
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                    style={t.status === "Open"
                                        ? { background: "rgba(74,150,131,0.15)", color: TEAL }
                                        : { background: "#F1F5F4", color: "#64748b" }}>{t.status}</span>
                            </td>
                            <td className="px-4 py-3 text-[12px] text-slate-500 hidden md:table-cell">{t.agent}</td>
                            <td className="px-4 py-3 text-[12px] text-slate-400 hidden md:table-cell">{t.date}<br />{t.time}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    </>
);

const OutboundScreen = () => (
    <>
        <PanelHead title="Outbound Calls" sub="Manage SIP trunks and place outbound calls" />
        <div className="space-y-3">
            {TRUNKS.map((t) => (
                <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4">
                    <span className="w-10 h-10 rounded-lg grid place-items-center text-white text-sm shrink-0 relative"
                        style={{ background: `linear-gradient(140deg, ${MOSS}, ${TEAL})` }}>
                        <i className="fa-solid fa-phone" />
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white" />
                    </span>
                    <div>
                        <div className="text-sm font-bold text-slate-900">{t.name}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{t.ip}</div>
                    </div>
                    <span className="text-[12px] font-mono px-2.5 py-1 rounded-lg" style={{ background: "rgba(35,95,82,0.08)", color: TEAL }}>{t.number}</span>
                    <span className="text-[11px] font-medium text-emerald-600"><i className="fa-solid fa-wave-square mr-1" />Active</span>
                    <span className="ml-auto text-[12px] font-semibold" style={{ color: TEAL }}>Make Call →</span>
                </div>
            ))}
        </div>
    </>
);

const InboundScreen = () => (
    <>
        <PanelHead title="Inbound Calls" sub="Map phone numbers to your AI agents for incoming calls" />
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4 flex items-center gap-3">
            <i className="fa-solid fa-magnifying-glass text-slate-300 text-sm" />
            <span className="text-sm text-slate-400">Search by phone number, agent, industry, or role…</span>
            <span className="ml-auto text-[11px] text-slate-400">3 mappings</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {[
                ["00911150000201", "Meridian Health (Appointments)", "Medical Services"],
                ["00911150000202", "Atlas Bank — Loan Recovery", "Financial Services"],
                ["00911150000203", "Nova Telecom — Support", "Telecom"],
            ].map(([num, agent, ind]) => (
                <div key={num} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3.5">
                    <span className="w-9 h-9 rounded-lg grid place-items-center text-white text-xs shrink-0"
                        style={{ background: `linear-gradient(140deg, ${MOSS}, ${TEAL})` }}>
                        <i className="fa-solid fa-phone-volume" />
                    </span>
                    <div className="font-mono text-[13px] text-slate-800">{num}</div>
                    <div className="ml-auto text-right min-w-0">
                        <div className="text-[13px] font-semibold text-slate-800 truncate max-w-[210px] sm:max-w-none">{agent}</div>
                        <div className="text-[11px] text-slate-400">{ind}</div>
                    </div>
                </div>
            ))}
        </div>
    </>
);

const CampaignsScreen = () => (
    <>
        <PanelHead title="Bulk Call Campaigns" sub="Create and manage bulk outbound campaigns from CSV data" />
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
            {[["Total Campaigns", "12", "fa-file-lines"], ["Active", "3", "fa-play"], ["Scheduled", "2", "fa-clock"], ["Completed", "7", "fa-circle-check"]].map(([l, v, ic]) => (
                <div key={l} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between">
                    <div>
                        <div className="text-[11px] text-slate-400 mb-1">{l}</div>
                        <div className="text-2xl font-bold text-slate-900">{v}</div>
                    </div>
                    <span className="w-9 h-9 rounded-lg grid place-items-center text-white text-sm" style={{ background: `linear-gradient(140deg, ${MOSS}, ${TEAL})` }}>
                        <i className={`fa-solid ${ic}`} />
                    </span>
                </div>
            ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[380px]">
                <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-slate-400 text-left bg-slate-50">
                        <th className="px-4 py-3 font-medium">Campaign</th>
                        <th className="px-4 py-3 font-medium">Recipients</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium hidden md:table-cell">Progress</th>
                    </tr>
                </thead>
                <tbody>
                    {[
                        ["Renewal Reminders — Q3", "2,400", "Active", 64],
                        ["Payment Follow-ups", "1,180", "Scheduled", 0],
                        ["Feedback Survey", "3,050", "Completed", 100],
                    ].map(([name, rec, st, pct]) => (
                        <tr key={name} className="border-t border-slate-100">
                            <td className="px-4 py-3 font-medium text-slate-800">{name}</td>
                            <td className="px-4 py-3 text-slate-500">{rec}</td>
                            <td className="px-4 py-3">
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(74,150,131,0.15)", color: TEAL }}>{st}</span>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell w-40">
                                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${TEAL}, ${MOSS})` }} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    </>
);

const SCREENS = {
    analytics: AnalyticsScreen,
    conversation: ConversationScreen,
    agents: AgentsScreen,
    knowledge: KnowledgeScreen,
    calls: CallsScreen,
    tickets: TicketsScreen,
    outbound: OutboundScreen,
    inbound: InboundScreen,
    campaigns: CampaignsScreen,
};

export const ProductTour = () => {
    const [active, setActive] = React.useState("analytics");
    const Screen = SCREENS[active];
    const groups = [...new Set(NAV.map((n) => n.group))];

    return (
        <section
            id="nyra-console"
            className="py-24 md:py-32 relative overflow-hidden scroll-mt-20"
            style={{
                background:
                    "radial-gradient(55% 40% at 100% 0%, rgba(95,168,138,0.18) 0%, transparent 55%), " +
                    "linear-gradient(180deg, #F5F3E7 0%, #E9E4CB 100%)",
            }}
            data-testid="product-tour"
        >
            <div className="container-x">
                <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
                    <Reveal>
                        <div className="text-sm text-slate-500 mb-4">The Nyra AI Console</div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl leading-tight">
                            See Nyra <span className="italic">in action.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed">
                            One console for every agent, channel, and decision — build agents,
                            ground them in your knowledge, run campaigns, and audit every call.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <div
                        className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                        style={{ boxShadow: "0 40px 100px -40px rgba(35,95,82,0.35)" }}
                    >
                        {/* browser chrome */}
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/70">
                            <span className="w-3 h-3 rounded-full bg-slate-300" />
                            <span className="w-3 h-3 rounded-full bg-slate-300" />
                            <span className="w-3 h-3 rounded-full bg-slate-300" />
                            <span className="ml-3 text-[12px] text-slate-400 flex items-center gap-2">
                                <i className="fa-solid fa-lock text-[10px]" /> Nyra AI · Console
                            </span>
                        </div>

                        <div className="grid md:grid-cols-[220px_1fr]">
                            {/* sidebar */}
                            <div className="border-b md:border-b-0 md:border-r border-slate-100 bg-slate-50/40 p-3 min-w-0">
                                <div className="flex flex-wrap md:flex-col gap-1.5 md:gap-1">
                                    {groups.map((g) => (
                                        <div key={g} className="contents md:block md:mb-2">
                                            <div className="hidden md:block text-[10px] uppercase tracking-wider text-slate-400 px-3 pt-2 pb-1">{g}</div>
                                            {NAV.filter((n) => n.group === g).map((n) => (
                                                <button
                                                    key={n.key}
                                                    onClick={() => setActive(n.key)}
                                                    data-testid={`tour-nav-${n.key}`}
                                                    className={`w-auto md:w-full flex items-center gap-2 md:gap-2.5 px-3 py-2 rounded-full md:rounded-lg text-[12px] md:text-[13px] font-medium transition-all whitespace-nowrap border md:border-0 ${
                                                        active === n.key
                                                            ? "text-white shadow-sm border-transparent"
                                                            : "text-slate-600 hover:bg-white bg-white md:bg-transparent border-slate-200"
                                                    }`}
                                                    style={active === n.key ? { background: TEAL } : {}}
                                                >
                                                    <i className={`${n.icon} text-xs w-4 text-center ${active === n.key ? "text-white" : "text-slate-400"}`} />
                                                    <span>{n.label}</span>
                                                    {n.badge && (
                                                        <span className={`ml-auto text-[10px] px-1.5 rounded-full ${active === n.key ? "bg-white/20" : "bg-slate-200 text-slate-500"}`}>{n.badge}</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* main panel */}
                            <div className="p-5 md:p-8 min-h-[520px] min-w-0">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={active}
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <Screen />
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
